import { memo } from "react";
import { cn } from "@/lib/utils";
import {
  BarChart3,
  BookOpen,
  History,
  Inbox,
  Users,
  UserCircle,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import type { AdminSidebarStats, AdminView } from "../admin/types";

export type { AdminView };

interface AdminSidebarProps {
  activeView: AdminView;
  onViewChange: (view: AdminView) => void;
  stats: AdminSidebarStats;
  collapsed: boolean;
  onToggleCollapse: () => void;
}

const menuItems: {
  section: string;
  items: {
    id: AdminView;
    label: string;
    icon: React.ElementType;
    color?: string;
    badgeKey?: string;
  }[];
}[] = [
  {
    section: "Overview",
    items: [
      { id: "reports", label: "Reports & Dashboard", icon: BarChart3 },
      {
        id: "activity-module",
        label: "Activity Module",
        icon: History,
      },
    ],
  },
  {
    section: "Data Management",
    items: [
      {
        id: "word-master",
        label: "Word Master",
        icon: BookOpen,
        color: "text-primary",
      },
    ],
  },
  {
    section: "Contributions",
    items: [
      {
        id: "contributions",
        label: "All Contributions",
        icon: Inbox,
        badgeKey: "pendingContributions",
      },
    ],
  },
  {
    section: "Users",
    items: [{ id: "users", label: "User Management", icon: Users }],
  },
  {
    section: "Account",
    items: [
      {
        id: "my-profile",
        label: "My profile",
        icon: UserCircle,
        color: "text-primary",
      },
    ],
  },
];

function AdminSidebarInner({
  activeView,
  onViewChange,
  stats,
  collapsed,
  onToggleCollapse,
}: AdminSidebarProps) {
  const getBadgeCount = (item: (typeof menuItems)[0]["items"][0]) => {
    if (item.badgeKey === "pendingContributions") {
      return stats.pendingContributions;
    }
    return 0;
  };

  return (
    <aside
      className={cn(
        "flex h-full min-h-0 shrink-0 flex-col bg-[hsl(220,40%,11%)] text-white transition-[width] duration-300 ease-out",
        collapsed ? "w-16" : "w-64"
      )}
    >
      <div className="flex items-center justify-between border-b border-white/10 p-4">
        {!collapsed && (
          <div>
            <h2 className="font-serif text-lg font-bold text-white">Admin Panel</h2>
            <p className="text-xs text-white/50">Jain Lexicon</p>
          </div>
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggleCollapse}
          className="h-8 w-8 text-white/70 hover:bg-white/10 hover:text-white"
        >
          {collapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <ChevronLeft className="h-4 w-4" />
          )}
        </Button>
      </div>

      <nav className="flex-1 space-y-6 overflow-y-auto py-4">
        {menuItems.map(section => (
          <div key={section.section}>
            {!collapsed && (
              <p className="mb-2 px-4 text-xs font-semibold uppercase tracking-wider text-white/40">
                {section.section}
              </p>
            )}
            <div className="space-y-1 px-2">
              {section.items.map(item => {
                const isActive = activeView === item.id;
                const badgeCount = getBadgeCount(item);
                return (
                  <button
                    key={item.id}
                    onClick={() => onViewChange(item.id)}
                    title={collapsed ? item.label : undefined}
                    className={cn(
                      "flex w-full items-center gap-3 rounded-lg text-left transition-all duration-200",
                      collapsed ? "justify-center p-3" : "px-3 py-2.5",
                      isActive
                        ? "bg-primary text-white shadow-lg"
                        : "hover:bg-white/8 text-white/70 hover:text-white"
                    )}
                  >
                    <item.icon
                      className={cn(
                        "h-5 w-5 shrink-0",
                        !isActive && item.color
                      )}
                    />
                    {!collapsed && (
                      <>
                        <span className="flex-1 text-sm font-medium">
                          {item.label}
                        </span>
                        {badgeCount > 0 && (
                          <span
                            className={cn(
                              "rounded-full px-2 py-0.5 text-xs font-bold",
                              isActive
                                ? "bg-white/20"
                                : "bg-yellow-500/80 text-white"
                            )}
                          >
                            {badgeCount}
                          </span>
                        )}
                      </>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </nav>
    </aside>
  );
}

/** Memoized so table pagination / local UI state do not repaint the nav when the parent re-renders. */
export const AdminSidebar = memo(AdminSidebarInner);
